/**
 * useAudience -- Hook reusable para filtrar UI segun audiencia del usuario.
 * PANEL-ARTIFACTS-AUDIENCE-V1-001 / T2.
 *
 * Concepto: cada artefacto del panel tiene metadata.audience_tag (business |
 * nexo | both). El usuario tiene una audience efectiva derivada de su role:
 *   - admin / dev               -> 'all'      (ve todo)
 *   - manager (NO business)     -> 'all'      (admin Nexo)
 *   - manager (business locked) -> 'business' (manager de un negocio)
 *   - member                    -> 'nexo'     (interno Nexo, no admin)
 *   - client                    -> 'business' (cliente final)
 *
 * Uso:
 *   import { resolveUserAudience, audienceMatches } from '../shared/useAudience.js';
 *   const audience = resolveUserAudience();           // 'all' | 'business' | 'nexo'
 *   const visible = audienceMatches('business', audience); // true si user ve business
 *
 * Para listas de elementos taggeados con audience_tag:
 *   const visibleItems = items.filter(i => audienceMatches(i.audience_tag, audience));
 *
 * Tambien expone fetchArtifactsByAudience() que consulta el endpoint backend.
 *
 * @module presentation/shared/useAudience
 */

import { AppConfig } from '../../config.js';
import { ArtifactsApiClient } from '../../infrastructure/api/ArtifactsApiClient.js';

/**
 * Determina si el usuario actual es business-locked (manager de un negocio
 * lockeado, NO admin Nexo). Mismo concepto que isBusinessUser() en NavBar.js
 * y main.js, centralizado aqui.
 */
function isBusinessLocked() {
  const locked = localStorage.getItem('nx_business_locked') === 'true';
  const role = AppConfig.role;
  return locked && role !== 'dev' && role !== 'admin';
}

/**
 * Resuelve la audiencia efectiva del usuario actual a partir de su role +
 * business_locked flag.
 * @returns {'all' | 'business' | 'nexo'}
 */
export function resolveUserAudience() {
  const role = AppConfig.role;

  // Admin / dev ven todo (no se filtra audience).
  if (role === 'dev' || role === 'admin') return 'all';

  // Manager con business lockeado: cliente operativo
  if (isBusinessLocked()) return 'business';

  // Manager Nexo (sin business lockeado): admin interno -> ve todo
  if (role === 'manager') return 'all';

  // Member: equipo interno Nexo (sin admin tools)
  if (role === 'member') return 'nexo';

  // Client: cliente final negocio
  if (role === 'client') return 'business';

  // Default seguro: business
  return 'business';
}

/**
 * Determina si un audience_tag de artefacto es visible para una audience efectiva.
 *   - artifactTag 'both' -> visible para cualquier audience
 *   - userAudience 'all' -> ve todo (dev/admin/manager-nexo)
 *   - artifactTag 'business' -> visible si userAudience es 'all' o 'business'
 *   - artifactTag 'nexo'     -> visible si userAudience es 'all' o 'nexo'
 *
 * @param {string} artifactTag - 'business' | 'nexo' | 'both'
 * @param {string} userAudience - 'all' | 'business' | 'nexo'
 * @returns {boolean}
 */
export function audienceMatches(artifactTag, userAudience) {
  if (!artifactTag || artifactTag === 'both') return true;
  if (userAudience === 'all') return true;
  return artifactTag === userAudience;
}

/**
 * Fetcha el catalogo de artefactos del backend filtrado por la audience del
 * usuario actual. Util para vistas admin de gestion de visibility.
 *
 * @returns {Promise<{ ok:boolean, audience?:string, artifacts?:Array, error? }>}
 */
export async function fetchArtifactsByAudience() {
  const audience = resolveUserAudience();
  // Si user es 'all' (dev/admin), no filtramos -> traemos todos
  const apiAudience = audience === 'all' ? undefined : audience;
  const res = await ArtifactsApiClient.list(apiAudience);
  if (!res.ok) return { ok: false, error: res.error };
  const payload = res.data?.data ?? res.data ?? {};
  return {
    ok: true,
    audience,
    artifacts: payload.artifacts || [],
    totalCount: payload.totalCount || 0
  };
}
